import { FC, useEffect, useRef, useState } from 'react';
import { Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import { QuestionnaireResponse, Patient, Encounter } from '@medplum/fhirtypes';
import { QuestionnaireForm } from '@medplum-components/QuestionnaireForm/QuestionnaireForm';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { areQuestionnaireResponsesEqual } from '@/utils/questionnaire-response.utils';
import { usePerinatalPeriodResponse } from '../hooks/usePerinatalPeriodResponse';
import { useQuestionnaireSubmitHandler } from '../hooks/useQuestionnaireSubmitHandler';
interface PerinatalPeriodModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  patient: Patient;
  encounter?: Encounter | null;
  questionnaireResponse?: QuestionnaireResponse | null;
}

export const PerinatalPeriodModal: FC<PerinatalPeriodModalProps> = ({
  opened,
  onClose,
  onSuccess,
  patient,
  encounter,
  questionnaireResponse,
}) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages', 'common']);
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const { questionnaire } = usePerinatalPeriodResponse(patient);
  const [formResponse, setFormResponse] = useState<
    QuestionnaireResponse | undefined
  >(undefined);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const handleSubmit = useQuestionnaireSubmitHandler({
    medplum,
    patient,
    encounter: encounter || undefined,
    questionnaire,
    questionnaireResponse: questionnaireResponse || undefined,
    status: 'completed',
  });

  const initialFormStateRef = useRef<QuestionnaireResponse | undefined>(
    undefined
  );

  // Reset form state when modal opens
  useEffect(() => {
    if (opened) {
      setFormResponse(undefined);
      initialFormStateRef.current = undefined;
      setFormKey((prev) => prev + 1);
    }
  }, [opened, questionnaireResponse]);

  const handleFormChange = (response: QuestionnaireResponse) => {
    // Capture initial state on first change
    if (!initialFormStateRef.current) {
      initialFormStateRef.current = response;
    }
    setFormResponse(response);
  };

  // Change detection
  const hasUnsavedChanges =
    formResponse &&
    initialFormStateRef.current &&
    !areQuestionnaireResponsesEqual(formResponse, initialFormStateRef.current);

  const handleSave = async () => {
    if (!formResponse || saving || !questionnaire) {
      return;
    }

    setSaving(true);
    try {
      await handleSubmit(formResponse);
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is already handled by the hook's notification
      console.error('Failed to save perinatal period:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (initialFormStateRef.current) {
      setFormResponse(initialFormStateRef.current);
    } else {
      setFormResponse(undefined);
    }
    setFormKey((prev) => prev + 1);
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={t('perinatalPeriod.modalTitle', {
        ns: 'patient-pages',
        defaultValue: 'Perinatal Period',
      })}
      size="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        {questionnaireResponse?.meta?.lastUpdated && (
          <Text size="sm" c="dimmed">
            {t('perinatalPeriod.lastUpdated', {
              ns: 'patient-pages',
              defaultValue: 'Last updated',
            })}
            :{' '}
            {new Date(questionnaireResponse.meta.lastUpdated).toLocaleString()}
          </Text>
        )}

        {questionnaire && (
          <QuestionnaireForm
            key={formKey}
            questionnaire={questionnaire}
            questionnaireResponse={questionnaireResponse || undefined}
            onChange={handleFormChange}
            excludeButtons
          />
        )}

        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={!!hasUnsavedChanges}
          isLoading={saving}
          isFormValid={!!formResponse}
          saveLabel={t('common:save', { defaultValue: 'Save' })}
        />
      </Stack>
    </Modal>
  );
};

export default PerinatalPeriodModal;
